import { fetchWithAdmin } from './fetchHelper';

export type Publisher =
  | { kind: 'partner'; id: string }
  | { kind: 'us' }
  | { kind: 'student'; userId: string };

export type Destination =
  | { kind: 'chapter-practice'; questionSetId: string; videoUrl?: string; chapterNumber?: string }
  | { kind: 'question-set'; questionSetId: string }
  | { kind: 'simulation'; simulationId: string }
  | { kind: 'route'; path: string }
  | { kind: 'site'; url: string };

export interface PublishedLink {
  id: string;
  slug: string;
  destination: Destination;
  publisher: Publisher;
  campaign?: string;
  variant?: string;
  guest?: { grade?: number; board?: string; subjects?: string[]; locale?: string };
  landing?: { headline: string; body?: string; imageUrl?: string; ctaLabel?: string };
  startsAt?: string;
  expiresAt?: string;
  status: 'active' | 'retired';
  guestLimit?: number;
  notes?: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  /** Added by the list: opens in the window it reports, and the set's title. */
  opens?: number;
  /** Added by the list: of those opens, the landings that reported a first
   *  paint, and the wait for it (tap → first paint, ms) at P50 / P70 / P90. */
  loads?: number;
  p50LoadMs?: number;
  p70LoadMs?: number;
  p90LoadMs?: number;
  destinationTitle?: string;
}

export const publishedLinksApi = {
  list: () => fetchWithAdmin<{ links: PublishedLink[]; opensWindowDays: number }>('/console/published-links'),

  create: (link: Partial<PublishedLink>) =>
    fetchWithAdmin<PublishedLink>('/console/published-links', { method: 'POST', body: JSON.stringify(link) }),

  /** Only the fields given are changed; null clears one. The slug never moves. */
  update: (slug: string, changes: Partial<PublishedLink>) =>
    fetchWithAdmin<PublishedLink>(`/console/published-links/${encodeURIComponent(slug)}`, {
      method: 'PUT', body: JSON.stringify(changes),
    }),

  /** The campaigns this link's follows carried — backend `admin/content/linkCampaigns.ts`. */
  campaigns: (slug: string, days: number) =>
    fetchWithAdmin<LinkCampaigns>(`/console/published-links/${encodeURIComponent(slug)}/campaigns?days=${days}`),
};

/** One campaign seen on a link's follows. */
export interface LinkCampaignRow {
  /** The follow's own `utm_campaign`, or `(none)`. */
  campaign: string;
  follows: number;
  /** Follows whose landing page loaded and claimed the arrival. */
  landed: number;
  /** `source / medium` pairs seen, most common first. */
  sources: string[];
  /** Ads (`utm_content`) seen, most common first. */
  variants: string[];
  lastFollowedAt: string;
}

export interface LinkCampaigns {
  slug: string;
  days: number;
  /** The campaign the link itself declares. */
  declared?: string;
  follows: number;
  rows: LinkCampaignRow[];
  /** Follows tagged with a campaign other than the declared one. */
  otherCampaignFollows: number;
}

/** A published URL still followed that goes nowhere — backend `admin/content/deadLinks.ts`. */
export interface DeadLink {
  slug: string;
  /** retired / ended: a link that exists; unknown: one that never did; active: dead when followed, live now. */
  status: 'retired' | 'ended' | 'unknown' | 'active';
  follows: number;
  lastFollowedAt: string;
  topReferrer?: string;
}

export const deadLinksApi = {
  list: (days: number) => fetchWithAdmin<{ days: number; links: DeadLink[] }>(`/console/dead-links?days=${days}`),
};
